import { Component, inject, OnDestroy } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ArrowLeft, MailIcon, BookOpen, File, LoaderCircle, LucideAngularModule } from 'lucide-angular';

import { SystemConfigurationService } from '@app/core/api/system-configuration/system-configuration.service';
import { AuthService } from '@app/core/api/auth/auth.service';
import { FileUploadService } from '@app/core/api/files/file-upload.service';
import { Subject, takeUntil } from 'rxjs';
import { convertToDownloadableLink, isUploadedConfigFileUrl, isUsableConfigUrl } from '@app/helpers/utils/urlFormat.utils';
import { InvestEntityType } from '@app/enums/InvestEntity.enum';
import { getUpperUserType, UserType } from '@app/enums/UserType.enum';

type ManualEntity = InvestEntityType;

@Component({
    selector: 'app-about-us',
    standalone: true,
    imports: [LucideAngularModule, RouterLink],
    templateUrl: './about-us.component.html',
    styleUrls: ['./about-us.component.scss'],
})
export class AboutUsComponent implements OnDestroy {
    readonly ArrowLeft = ArrowLeft;
    readonly MailIcon = MailIcon;
    readonly BookOpen = BookOpen;
    readonly File = File;
    readonly LoaderCircle = LoaderCircle;
    readonly InvestEntityType = InvestEntityType;

    systemConfigService = inject(SystemConfigurationService);
    fileUploadService = inject(FileUploadService);
    authService = inject(AuthService);

    destroy$ = new Subject<void>();

    dataPrivacyUrl: string = '#';
    guidelinesUrl: string = '#';

    isLoadingDataPrivacy = false;
    isLoadingGuidelines = false;
    isLoadingUserManual = false;
    manualEntity: ManualEntity = InvestEntityType.INVESTOR;

    get isAuthenticated(): boolean {
        return !!this.authService.user();
    }

    get isExternalUser(): boolean {
        return getUpperUserType(this.authService.user()?.user_type) === UserType.EXTERNAL;
    }

    get showManualSegment(): boolean {
        return !this.isExternalUser;
    }

    get termsLink(): string {
        return this.isAuthenticated ? '/help/terms-and-conditions' : '/terms-and-conditions';
    }

    get isInvesteeUser(): boolean {
        const entityType = (this.authService.user()?.type || '').toLowerCase();

        return entityType === InvestEntityType.INVESTEE;
    }

    get activeManualEntity(): ManualEntity {
        if (this.isExternalUser) {
            return this.isInvesteeUser ? InvestEntityType.INVESTEE : InvestEntityType.INVESTOR;
        }

        return this.manualEntity;
    }

    get userManualInvestEntityTypeLabel(): string {
        const entity = this.activeManualEntity;

        return entity.charAt(0).toUpperCase() + entity.slice(1);
    }

    get userManualConfigKey(): `user_manual_${ManualEntity}` {
        return `user_manual_${this.activeManualEntity}`;
    }

    setManualEntity(entity: ManualEntity): void {
        this.manualEntity = entity;
    }

    fetchConfigUrl(key: string, callback: (url: string) => void): void {
        this.systemConfigService
            .getSysConfigByKey(key)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    const url = response.data || '#';
                    callback(url);
                },
                error: (error) => {
                    console.error(`Failed to load ${key}:`, error);
                    callback('#');
                },
            });
    }

    onDataPrivacyClick(): void {
        if (this.isLoadingDataPrivacy) return;

        this.isLoadingDataPrivacy = true;

        // Open window immediately for iOS Safari compatibility
        const newWindow = window.open('', '_blank');

        this.fetchConfigUrl('data_privacy', (url) => {
            this.dataPrivacyUrl = url;
            this.isLoadingDataPrivacy = false;

            if (newWindow) {
                if (url && url !== '#' && url.trim() !== '') {
                    newWindow.location.href = url;
                } else {
                    newWindow.close();
                    console.warn('Invalid data privacy URL:', url);
                }
            }
        });
    }

    onGuidelinesClick(): void {
        if (this.isLoadingGuidelines) return;

        this.isLoadingGuidelines = true;

        // Open window immediately for iOS Safari compatibility
        const newWindow = window.open('', '_blank');

        this.fetchConfigUrl('guidelines', (url) => {
            this.guidelinesUrl = url;
            this.isLoadingGuidelines = false;

            if (newWindow) {
                if (isUsableConfigUrl(url)) {
                    newWindow.location.href = url;
                } else {
                    newWindow.close();
                    console.warn('Invalid guidelines URL:', url);
                }
            }
        });
    }

    onUserManualClick(): void {
        if (this.isLoadingUserManual) return;

        this.isLoadingUserManual = true;
        const newWindow = window.open('', '_blank');

        this.fetchConfigUrl(this.userManualConfigKey, (url) => {
            this.isLoadingUserManual = false;

            if (!isUsableConfigUrl(url)) {
                newWindow?.close();
                return;
            }

            if (isUploadedConfigFileUrl(url)) {
                newWindow?.close();
                const downloadUrl = this.fileUploadService.getDownloadUrl(
                    this.convertToDownloadableLink(url),
                    'PDIC-EPIC-User-Manual.pdf',
                );
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                return;
            }

            if (newWindow) {
                newWindow.location.href = url;
            }
        });
    }

    convertToDownloadableLink(url: string): string {
        return convertToDownloadableLink(url);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
